import React from "react";
import { Alert, Snackbar } from "@mui/material";
import NotificationIcon from "@mui/icons-material/Notifications";
import CelebrationIcon from "@mui/icons-material/Celebration";
import { JobMonitorDrawer } from "./components";
import { useJobMonitorState } from "./hooks";

// Shape of the job monitor UI state so it can be shared across components
export type JobMonitorState = ReturnType<typeof useJobMonitorState>;

interface JobMonitorProps {
  state: JobMonitorState;
}

// Pure presentational component that renders the monitor using provided state
export const JobMonitorView: React.FC<JobMonitorProps> = ({ state }) => {
  const {
    open,
    toggleDrawer,
    showToast,
    toastMessage,
    handleCloseToast,
    showCompletionToast,
    setShowCompletionToast,
    retryingJob,
    activeFilter,
    setActiveFilter,
    jobCounts,
    filteredJobs,
    newJobIds,
    completedJobIds,
    handleRemoveJob,
    handleRetryJob,
    handleJobClick,
    isMobile,
    activeJobsCount,
  } = state;

  return (
    <>
      <Snackbar
        open={showToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="info"
          sx={{ alignItems: "center" }}
          icon={<NotificationIcon />}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showCompletionToast}
        autoHideDuration={4000}
        onClose={() => setShowCompletionToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowCompletionToast(false)}
          severity="success"
          sx={{ alignItems: "center" }}
          icon={<CelebrationIcon />}
        >
          Report completed! Click to view.
        </Alert>
      </Snackbar>

      <JobMonitorDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        jobCounts={jobCounts}
        filteredJobs={filteredJobs}
        handleRemoveJob={handleRemoveJob}
        handleRetryJob={handleRetryJob}
        handleJobClick={handleJobClick}
        newJobIds={newJobIds}
        completedJobIds={completedJobIds}
        retryingJob={retryingJob}
        isMobile={isMobile}
        activeJobsCount={activeJobsCount}
      />
    </>
  );
};

// Default export keeps backward compatibility by owning its own state
const JobMonitor: React.FC = () => {
  const state = useJobMonitorState();
  return <JobMonitorView state={state} />;
};

export default JobMonitor;
